import React, {
  createContext,
  useContext,
  PropsWithChildren,
  useMemo,
} from "react";
import useSWR from "swr";

// Feature flag for enabling credentials in fetch requests
declare const __ENABLE_FETCH_WITH_CREDENTIALS__: boolean | undefined;

type SessionUser = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

type SessionData = {
  user?: SessionUser;
} | null;

type SessionDataContextType = {
  sessionData: SessionData;
  isLoading: boolean;
  error: Error | undefined;
};

const SessionDataContext = createContext<SessionDataContextType | undefined>(
  undefined,
);

type SessionDataProviderProps = PropsWithChildren<{
  sessionDataUrl?: string;
}>;

/**
 * fetcher - Retrieves session data from a backend API endpoint.
 *
 * Makes a JSON request to fetch current user session information. Optionally includes
 * credentials (cookies) based on the __ENABLE_FETCH_WITH_CREDENTIALS__ feature flag.
 * Handles "not-found" responses gracefully by returning null instead of throwing.
 * Validates response is JSON to prevent parsing errors.
 */
const fetcher = async (url: string): Promise<SessionData> => {
  const options: RequestInit = {
    headers: {
      accept: "application/json",
    },
    cache: "no-cache",
  };

  // Check if credentials should be included (feature flag)
  if (
    typeof __ENABLE_FETCH_WITH_CREDENTIALS__ !== "undefined" &&
    __ENABLE_FETCH_WITH_CREDENTIALS__
  ) {
    options.credentials = "include";
  }

  const res = await fetch(url, options);

  if (!res.ok) {
    throw new Error("Failed to fetch session data");
  }

  const contentType = res.headers.get("content-type");
  if (!contentType?.includes("application/json")) {
    throw new Error("Session endpoint is not serving json");
  }

  const data = await res.json();

  // Handle "not found" error gracefully
  if (data.error === "not-found") {
    return null;
  }

  return data;
};

const SESSION_SWR_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
  onError: (err: Error) => {
    console.warn("Could not fetch session data due to error:", err);
  },
};

/**
 * SessionDataProvider - Context provider for managing user session data throughout the app.
 *
 * Fetches and caches session data using SWR (stale-while-revalidate) from the provided URL.
 * Exposes session data, loading state, and errors through context. Use this at the app root
 * level and access via useSessionData() hook in child components. Disables auto-revalidation
 * to avoid excessive network requests - session data is fetched once on mount.
 */
export const SessionDataProvider = ({
  children,
  sessionDataUrl,
}: SessionDataProviderProps) => {
  const { data, error, isLoading } = useSWR<SessionData>(
    sessionDataUrl || null,
    fetcher,
    SESSION_SWR_OPTIONS,
  );

  const contextValue = useMemo(
    () => ({
      sessionData: data ?? null,
      isLoading,
      error,
    }),
    [data, isLoading, error],
  );

  return (
    <SessionDataContext.Provider value={contextValue}>
      {children}
    </SessionDataContext.Provider>
  );
};

/**
 * useSessionData - Hook to access session data from SessionDataProvider context.
 *
 * Returns the current user session data, loading state, and any errors from the provider.
 * Must be used within a SessionDataProvider or will throw an error. Use this hook in
 * components that need to read or display user session information.
 */
export const useSessionData = () => {
  const context = useContext(SessionDataContext);
  if (context === undefined) {
    throw new Error("useSessionData must be used within SessionDataProvider");
  }
  return context;
};

/**
 * useSessionDataDirect - Direct session data hook without requiring a provider (legacy).
 *
 * Fetches session data using SWR directly, bypassing the context provider pattern.
 * Provided for backward compatibility with code that doesn't use SessionDataProvider.
 * For new code, prefer using SessionDataProvider + useSessionData() for better
 * performance and centralized session management.
 */
export const useSessionDataDirect = (sessionDataUrl?: string) => {
  const { data, error, isLoading } = useSWR<SessionData>(
    sessionDataUrl || null,
    fetcher,
    SESSION_SWR_OPTIONS,
  );

  return {
    sessionData: data ?? null,
    isLoading,
    error,
  };
};
